import { useState } from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { changePassword } from "./services/auth";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Releases from "./pages/Releases";
import FeatureRequests from "./pages/FeatureRequests";
import BugReports from "./pages/BugReports";
import Versions from "./pages/Versions";
import Users from "./pages/Users";

const roleBadgeColors: Record<string, string> = {
  admin: "#ef4444",
  authorizer: "#f59e0b",
  user: "#3b82f6",
};

function AuthenticatedApp() {
  const { session, logout, role, can, notification, clearNotification } = useAuth();
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [changeError, setChangeError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  if (!session) return <Login />;

  // Force password change screen
  if (session.mustChangePassword && !changingPassword) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <h1>Change Your Password</h1>
            <p>Your account requires a password change before you can continue.</p>
          </div>
          {changeError && <div className="error-banner" style={{ marginBottom: "1rem" }}>⚠️ {changeError}</div>}
          <form onSubmit={async (e) => {
            e.preventDefault();
            setChangeError("");
            if (newPass.length < 8) { setChangeError("Password must be at least 8 characters"); return; }
            if (newPass !== confirmPass) { setChangeError("Passwords don't match"); return; }
            setChangingPassword(true);
            try {
              await changePassword(session.username, newPass);
              // Update the session object in memory so we don't loop
              session.mustChangePassword = false;
              setChangingPassword(false);
              // Force re-render with updated session
              window.location.reload();
            } catch (err: any) {
              setChangeError(err.message);
              setChangingPassword(false);
            }
          }} className="login-form">
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} required placeholder="At least 8 characters" />
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <input type="password" value={confirmPass} onChange={(e) => setConfirmPass(e.target.value)} required placeholder="Repeat password" />
            </div>
            <button type="submit" className="btn btn-primary login-btn" disabled={changingPassword}>
              {changingPassword ? "Updating..." : "Set New Password"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <nav className="sidebar">
        <div className="sidebar-header">
          <h1>BSDP</h1>
          <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>Business System Development Portal</span>
        </div>
        <ul className="nav-links">
          <li>
            <NavLink to="/">Dashboard</NavLink>
          </li>
          <li>
            <NavLink to="/releases">Releases</NavLink>
          </li>
          <li>
            <NavLink to="/versions">Versions</NavLink>
          </li>
          <li>
            <NavLink to="/features">Feature Requests</NavLink>
          </li>
          <li>
            <NavLink to="/bugs">Bug Reports</NavLink>
          </li>
          {can("admin:users") && (
            <li>
              <NavLink to="/users">Users</NavLink>
            </li>
          )}
        </ul>

        {/* User info at bottom */}
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-name">{session.username}</div>
            <div className="user-role">
              <span
                className="role-pill"
                style={{ backgroundColor: roleBadgeColors[role || "user"] }}
              >
                {role}
              </span>
            </div>
          </div>
          <button className="logout-btn" onClick={logout} title="Sign out">
            ↪ Sign Out
          </button>
        </div>
      </nav>
      <main className="content">
        {notification && (
          <div className="role-notification animate-slide-down">
            <span className="rn-icon">🔄</span>
            <span>{notification}</span>
            <button className="rn-close" onClick={clearNotification}>✕</button>
          </div>
        )}
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/releases" element={<Releases />} />
          <Route path="/versions" element={<Versions />} />
          <Route path="/features" element={<FeatureRequests />} />
          <Route path="/bugs" element={<BugReports />} />
          <Route path="/users" element={<Users />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AuthenticatedApp />
    </AuthProvider>
  );
}

export default App;
